/**
 * Store locator: fetches stores from app proxy, URL-synced search/country/page, sidebar panel.
 */

import {
  defaultCountryFilterOptions,
  renderCountryFilterChips,
  resolveCountryFilters,
} from './store-location-filters.js';
import { extractDataArray } from './store-location-data.js';
import {
  applyStorePagination,
  createStoreDrawer,
  renderStoreResults,
} from './store-location-ui.js';

/** @typedef {import('./store-location-data.js').StoreMeta} StoreMeta */

const SEARCH_DEBOUNCE_MS = 600;

/** @type {import('./store-location-ui.js').StoreLocationUiConfig} */
const UI_CONFIG = {
  bemPrefix: 'store-locator',
  hoursPrefix: 'store-locator',
  detailIdPrefix: 'StoreLocatorDetail',
  panelIdPrefix: 'StoreLocatorPanel',
  panelTitleIdPrefix: 'StoreLocatorPanelTitle',
  mapLinkClass: 'store-locator__map-link',
  mapIconWrapperClass: 'store-locator__map-icon',
  panelMapLinkClass: 'store-locator__map-link store-locator__panel-map-link',
  panelMapIconClass: 'store-locator__map-icon',
  mapIconInSpan: true,
  openClass: 'store-locator--drawer-open',
  scrollDatasetKey: 'storeLocatorScrollY',
  templatesSelector: '[data-store-locator-templates]',
};

class StoreLocatorComponent extends HTMLElement {
  /** @type {ReturnType<typeof createStoreDrawer> | null} */
  #drawer = null;
  /** @type {HTMLElement | null} */
  #overlay = null;
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {HTMLElement | null} */
  #panelBody = null;
  /** @type {HTMLButtonElement | null} */
  #closeBtn = null;
  /** @type {HTMLInputElement | null} */
  #search = null;
  /** @type {HTMLElement | null} */
  #grid = null;
  /** @type {HTMLElement | null} */
  #statusEl = null;
  /** @type {HTMLElement | null} */
  #emptyEl = null;
  /** @type {HTMLElement | null} */
  #paginationNav = null;
  /** @type {HTMLElement | null} */
  #loadingEl = null;
  /** @type {string | undefined} */
  #sectionId;
  /** @type {AbortController | null} */
  #fetchAbort = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #searchDebounce = null;
  /** @type {boolean} */
  #skipPopstate = false;
  /** @type {Record<string, string>} */
  #countryLabels = {};
  /** @type {string} */
  #mapIconHtml = '';
  /** @type {string} */
  #labelShowOnMap = '';
  /** @type {string} */
  #labelDeliveryHours = '';
  /** @type {string} */
  #labelOpeningHours = '';
  /** @type {string} */
  #labelCountryAll = '';
  /** @type {HTMLElement | null} */
  #filtersContainer = null;
  /** @type {import('./store-location-filters.js').CountryFilterOption[]} */
  #backendCountries = [];
  /** @type {StoreMeta | null} */
  #lastMeta = null;
  /** @type {boolean} */
  #scrollAfterPageChange = false;

  /** @param {KeyboardEvent} e */
  #onKeydown = (e) => {
    if (e.key !== 'Escape' || !this.#drawer?.isOpen()) return;
    e.preventDefault();
    this.#drawer.close();
  };

  #onPopstate = () => {
    if (this.#skipPopstate) return;
    this.#syncUIFromUrl();
    this.#loadStores();
  };

  connectedCallback() {
    if (this.dataset.designMode === 'true') return;

    this.#overlay = this.querySelector('[data-store-locator-overlay]');
    this.#panel = this.querySelector('[data-store-locator-panel]');
    this.#panelBody = this.querySelector('[data-store-locator-panel-body]');
    this.#closeBtn = this.querySelector('[data-store-locator-close]');
    this.#search = this.querySelector('[data-store-locator-search]');
    this.#grid = this.querySelector('[data-store-locator-grid]');
    this.#statusEl = this.querySelector('[data-store-locator-status]');
    this.#emptyEl = this.querySelector('[data-store-locator-empty]');
    this.#paginationNav = this.querySelector('[data-store-locator-pagination]');
    this.#loadingEl = this.querySelector('[data-store-locator-loading]');
    this.#sectionId = this.dataset.sectionId;
    this.#labelShowOnMap = this.dataset.labelShowOnMap || 'Show on map';
    this.#labelDeliveryHours = this.dataset.labelDeliveryHours || '';
    this.#labelOpeningHours = this.dataset.labelOpeningHours || '';
    this.#labelCountryAll = this.dataset.labelCountryAll || 'All';
    this.#filtersContainer = this.querySelector('[data-store-locator-filters]');

    const labelsScript = this.querySelector('[data-store-locator-country-labels]');
    if (labelsScript?.textContent) {
      try {
        this.#countryLabels = JSON.parse(labelsScript.textContent);
      } catch {
        this.#countryLabels = {};
      }
    }

    this.#backendCountries = defaultCountryFilterOptions(this.#countryLabels);

    const mapIconTpl = document.getElementById(`StoreLocatorMapIcon-${this.#sectionId}`);
    if (mapIconTpl) {
      this.#mapIconHtml = mapIconTpl.innerHTML;
    }

    if (this.#panel && this.#overlay && this.#panelBody) {
      this.#drawer = createStoreDrawer({
        root: this,
        panel: this.#panel,
        overlay: this.#overlay,
        panelBody: this.#panelBody,
        closeBtn: this.#closeBtn,
        config: UI_CONFIG,
      });
    }

    this.#closeBtn?.addEventListener('click', () => this.#drawer?.close());
    this.#overlay?.addEventListener('click', () => this.#drawer?.close());

    this.#filtersContainer?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const chip = target.closest('[data-filter-country]');
      if (!chip || !this.#filtersContainer?.contains(chip)) return;

      event.preventDefault();
      const code = chip.getAttribute('data-filter-country') || 'all';
      this.#setCountryFilter(code);
      this.#updateUrl({ page: 1 });
      void this.#loadStores();
    });

    this.#search?.addEventListener('input', () => {
      if (this.#searchDebounce != null) clearTimeout(this.#searchDebounce);
      this.#searchDebounce = setTimeout(() => {
        this.#searchDebounce = null;
        this.#updateUrl({ page: 1 });
        this.#loadStores();
      }, SEARCH_DEBOUNCE_MS);
    });

    this.#paginationNav?.querySelector('[data-store-locator-pagination-prev]')?.addEventListener('click', () => {
      const page = this.#getPageFromUrl();
      if (page > 1) {
        this.#scrollAfterPageChange = true;
        this.#updateUrl({ page: page - 1 });
        void this.#loadStores();
      }
    });

    this.#paginationNav?.querySelector('[data-store-locator-pagination-next]')?.addEventListener('click', () => {
      const page = this.#getPageFromUrl();
      const lastPage = this.#lastMeta?.last_page ?? 1;
      if (page < lastPage) {
        this.#scrollAfterPageChange = true;
        this.#updateUrl({ page: page + 1 });
        void this.#loadStores();
      }
    });

    document.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('popstate', this.#onPopstate);

    this.#renderCountryFilters();
    this.#syncUIFromUrl();
    void this.#loadStores();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('popstate', this.#onPopstate);
    if (this.#searchDebounce != null) clearTimeout(this.#searchDebounce);
    this.#fetchAbort?.abort();
  }

  #getApiUrl() {
    const path = this.dataset.storesApiPath || '/apps/k-design-1/stores';
    return new URL(path, window.location.origin);
  }

  #getSearchFromUrl() {
    return new URL(window.location.href).searchParams.get('search')?.trim() || '';
  }

  #getCountryFromUrl() {
    return new URL(window.location.href).searchParams.get('countryCode')?.trim().toUpperCase() || '';
  }

  #getPageFromUrl() {
    const p = Number.parseInt(
      new URL(window.location.href).searchParams.get('page') || '1',
      10
    );
    return Number.isNaN(p) || p < 1 ? 1 : p;
  }

  #syncUIFromUrl() {
    const search = this.#getSearchFromUrl();
    const country = this.#getCountryFromUrl();
    if (this.#search) this.#search.value = search;
    this.#renderCountryFilters(country || 'all');
  }

  /**
   * @param {string} [activeCode]
   */
  #renderCountryFilters(activeCode) {
    if (!this.#filtersContainer || this.dataset.designMode === 'true') return;

    const active = activeCode || this.#getCountryFromUrl() || 'all';
    renderCountryFilterChips(
      this.#filtersContainer,
      this.#backendCountries,
      active,
      { all: this.#labelCountryAll },
      {
        chip: 'store-locator__chip',
        active: 'store-locator__chip--active',
      }
    );
  }

  /**
   * @param {Record<string, unknown>} body
   * @returns {boolean}
   */
  #applyCountryFiltersFromBody(body) {
    const { countries } = resolveCountryFilters(body, this.#countryLabels);
    this.#backendCountries = countries;
    this.#renderCountryFilters();

    const active = this.#getCountryFromUrl() || 'all';
    if (
      active !== 'all' &&
      this.#backendCountries.length > 0 &&
      !this.#backendCountries.some((item) => item.code === active)
    ) {
      return false;
    }

    return true;
  }

  /**
   * @param {{ page?: number }} overrides
   */
  #updateUrl(overrides = {}) {
    const url = new URL(window.location.href);
    const search = (this.#search?.value || '').trim();
    const chip = this.#filtersContainer?.querySelector(
      '[data-filter-country].store-locator__chip--active'
    );
    const country = chip?.getAttribute('data-filter-country') || 'all';

    if (search) {
      url.searchParams.set('search', search);
    } else {
      url.searchParams.delete('search');
    }

    if (country && country !== 'all') {
      url.searchParams.set('countryCode', country);
    } else {
      url.searchParams.delete('countryCode');
    }

    const page = overrides.page ?? this.#getPageFromUrl();
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    this.#skipPopstate = true;
    history.replaceState(null, '', url.toString());
    this.#skipPopstate = false;
  }

  #buildFetchParams() {
    const params = new URLSearchParams();
    const search = this.#getSearchFromUrl();
    const country = this.#getCountryFromUrl();
    const page = this.#getPageFromUrl();

    if (search) params.set('search', search);
    if (country) params.set('countryCode', country);
    if (page > 1) params.set('page', String(page));

    return params;
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    this.classList.toggle('store-locator--loading', loading);

    if (loading) {
      this.#loadingEl?.removeAttribute('hidden');
      this.#grid?.setAttribute('aria-busy', 'true');
      this.#search?.setAttribute('disabled', '');
      this.querySelectorAll('[data-filter-country]').forEach((chip) => {
        chip.setAttribute('disabled', '');
        chip.setAttribute('aria-disabled', 'true');
      });
      this.#paginationNav
        ?.querySelectorAll('button')
        .forEach((btn) => btn.setAttribute('disabled', ''));
    } else {
      this.#loadingEl?.setAttribute('hidden', '');
      this.#grid?.setAttribute('aria-busy', 'false');
      this.#search?.removeAttribute('disabled');
      this.querySelectorAll('[data-filter-country]').forEach((chip) => {
        chip.removeAttribute('disabled');
        chip.setAttribute('aria-disabled', 'false');
      });
      this.#paginationNav
        ?.querySelectorAll('button')
        .forEach((btn) => btn.removeAttribute('disabled'));
    }
  }

  async #loadStores() {
    const scrollAfterLoad = this.#scrollAfterPageChange;
    this.#scrollAfterPageChange = false;

    this.#fetchAbort?.abort();
    const controller = new AbortController();
    this.#fetchAbort = controller;

    this.#setLoading(true);
    this.#setError(false);
    this.#setEmpty(false);

    const apiUrl = this.#getApiUrl();
    const params = this.#buildFetchParams();
    const query = params.toString();
    if (query) apiUrl.search = query;

    try {
      const response = await fetch(apiUrl.toString(), {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const body = await response.json();
      if (controller.signal.aborted) return;

      if (!this.#applyCountryFiltersFromBody(body)) {
        const url = new URL(window.location.href);
        url.searchParams.delete('countryCode');
        this.#skipPopstate = true;
        history.replaceState(null, '', url.toString());
        this.#skipPopstate = false;
        this.#setCountryFilter('all');
        void this.#loadStores();
        return;
      }

      const data = extractDataArray(body);
      const meta = body.meta ?? {};
      this.#lastMeta = {
        current_page: Number(meta.current_page) || this.#getPageFromUrl(),
        last_page: Number(meta.last_page) || 1,
        per_page: Number(meta.per_page) || data.length || 20,
        total: Number(meta.total) || data.length,
      };

      this.#renderStores(data);
      this.#applyPagination(this.#lastMeta);

      if (data.length === 0) {
        this.#setEmpty(true);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.warn('Store locator: fetch failed', err);
      this.#renderStores([]);
      this.#applyPagination(null);
      this.#setError(true);
    } finally {
      if (!controller.signal.aborted) {
        this.#setLoading(false);
        if (scrollAfterLoad) {
          this.#scrollToPageTop();
        }
      }
    }
  }

  /**
   * @param {boolean} on
   */
  #setError(on) {
    if (!this.#statusEl) return;
    if (on) {
      this.#statusEl.textContent = this.dataset.labelError || '';
      this.#statusEl.hidden = false;
      this.#statusEl.classList.add('store-locator__status--error');
    } else {
      this.#statusEl.hidden = true;
      this.#statusEl.textContent = '';
      this.#statusEl.classList.remove('store-locator__status--error');
    }
  }

  /**
   * @param {boolean} on
   */
  #setEmpty(on) {
    if (this.#emptyEl) this.#emptyEl.hidden = !on;
    if (this.#grid) this.#grid.hidden = on;
  }

  /**
   * @param {unknown[]} data
   */
  #renderStores(data) {
    if (!this.#grid || !this.#sectionId) return;

    this.#drawer?.close();

    renderStoreResults(
      this,
      this.#grid,
      this.#sectionId,
      data,
      this.#countryLabels,
      UI_CONFIG,
      {
        showOnMap: this.#labelShowOnMap,
        deliveryHours: this.#labelDeliveryHours,
        openingHours: this.#labelOpeningHours,
      },
      this.#mapIconHtml,
      'Store locator',
      (index) => this.#drawer?.open(index)
    );
  }

  /**
   * @param {StoreMeta | null} meta
   */
  #applyPagination(meta) {
    applyStorePagination(this, meta, {
      nav: this.#paginationNav,
      prevSelector: '[data-store-locator-pagination-prev]',
      nextSelector: '[data-store-locator-pagination-next]',
      statusSelector: '[data-store-locator-pagination-status]',
      rangeTemplate: this.dataset.paginationRangeTemplate || '',
    });
  }

  #scrollToPageTop() {
    const anchor =
      this.querySelector('.store-locator__page-title') ??
      this.querySelector('.store-locator__content') ??
      this;
    const top = anchor.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    });
  }

  /**
   * @param {string} code
   */
  #setCountryFilter(code) {
    const normalized = (code || 'all').toUpperCase();
    const active = normalized === 'ALL' ? 'all' : normalized;
    this.#renderCountryFilters(active);
  }
}

if (!customElements.get('store-locator-component')) {
  customElements.define('store-locator-component', StoreLocatorComponent);
}
